import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { H1, Text } from '../components/ui/Typography';

const LegalPage = ({ type }) => {
  const { lang = 'nl' } = useParams();
  const isEn = lang === 'en';

  // Content Dictionary
  const content = {
    privacy: {
      title: isEn ? 'Privacy & Security' : 'Privacy & Beveiliging',
      metaTitle: isEn ? 'Privacy Policy | Testamentair.com - Secure Estate Planning' : 'Privacybeleid | Testamentair.com - Veilig uw nalatenschap voorbereiden',
      metaDesc: isEn ? 'Learn how Testamentair protects your sensitive data with bank-grade encryption and strict GDPR compliance during your estate planning.' : 'Ontdek hoe Testamentair uw gevoelige gegevens beschermt met bank-grade encryptie en strikte AVG-naleving tijdens uw nalatenschapsplanning.',
      intro: isEn 
        ? 'At Testamentair, privacy is not a feature—it is our foundation. We handle your sensitive information with the highest degree of confidentiality and security, ensuring your legacy preparation remains private.'
        : 'Bij Testamentair is privacy geen extra optie, maar de kern van ons bestaan. Wij behandelen uw gevoelige informatie met de hoogste graad van vertrouwelijkheid en veiligheid, zodat uw voorbereiding strikt privé blijft.',
      sections: [
        {
          h: isEn ? '1. What information do we collect?' : '1. Welke gegevens verzamelen wij?',
          p: isEn 
            ? 'To provide an accurate estate analysis, we collect information regarding your family structure, residency, and broad asset categories. We do not store specific account numbers or sensitive passwords. All data collected is strictly relevant to the strategic analysis of your legacy.'
            : 'Om een nauwkeurige nalatenschapsanalyse te kunnen bieden, verzamelen wij gegevens over uw gezinsstructuur, woonplaats en globale vermogenscategorieën. Wij slaan geen specifieke rekeningnummers of gevoelige wachtwoorden op. Alle verzamelde data is strikt relevant voor de strategische analyse van uw nalatenschap.'
        },
        {
          h: isEn ? '2. Why do we collect this information?' : '2. Waarom verzamelen wij gegevens?',
          p: isEn 
            ? 'The primary purpose of data collection is the generation of your Strategic Estate Dossier. This information allows our algorithms to identify fiscal risks, jurisdictional conflicts, and optimization opportunities specific to your situation.'
            : 'Het hoofddoel van gegevensverzameling is het genereren van uw Strategisch Nalatenschapsdossier. Deze informatie stelt onze algoritmen in staat om fiscale risico\'s, jurisdictieconflicten en optimalisatiekansen te identificeren die specifiek zijn voor uw situatie.'
        },
        {
          h: isEn ? '3. Data Minimization & Storage' : '3. Dataminimalisatie & Opslag',
          p: isEn 
            ? 'We adhere to the principle of data minimization: we only ask for what is necessary. Your data is stored on secure, encrypted servers located within the European Economic Area (EEA), ensuring full compliance with European standards.'
            : 'Wij hanteren het principe van dataminimalisatie: wij vragen alleen wat strikt noodzakelijk is. Uw gegevens worden opgeslagen op beveiligde, versleutelde servers binnen de Europese Economische Ruimte (EER), in overeenstemming met de Europese standaarden.'
        },
        {
          h: isEn ? '4. Encryption & Security' : '4. Encryptie & Beveiliging',
          p: isEn 
            ? 'Your data is protected by bank-grade SSL/TLS encryption during transit and AES-256 encryption at rest. We conduct regular security audits to ensure our platform remains resilient against emerging threats.'
            : 'Uw gegevens worden beschermd door bank-grade SSL/TLS-encryptie tijdens verzending en AES-256-encryptie bij opslag. Wij voeren regelmatig beveiligingsaudits uit om de weerbaarheid van ons platform tegen nieuwe dreigingen te garanderen.'
        },
        {
          h: isEn ? '5. Your GDPR Rights' : '5. Uw rechten onder de AVG/GDPR',
          p: isEn 
            ? 'Under the General Data Protection Regulation, you have the right to access, rectify, or erase your personal data. You also have the right to restrict processing and the right to data portability. Requests can be submitted directly through our contact page.'
            : 'Onder de Algemene Verordening Gegevensbescherming heeft u recht op inzage, rectificatie of verwijdering van uw persoonsgegevens. Daarnaast heeft u het recht op beperking van verwerking en overdraagbaarheid van data. Verzoeken kunnen rechtstreeks via onze contactpagina worden ingediend.'
        }
      ]
    },
    cookies: {
      title: isEn ? 'Cookie Policy' : 'Cookiebeleid',
      metaTitle: isEn ? 'Cookie Policy | Testamentair.com' : 'Cookiebeleid | Testamentair.com',
      metaDesc: isEn ? 'Information about the technical and analytical cookies we use to optimize your experience on Testamentair.' : 'Informatie over de technische en analytische cookies die wij gebruiken om uw ervaring op Testamentair te optimaliseren.',
      intro: isEn 
        ? 'We use cookies to ensure that our platform functions correctly, to remember your preferences, and to understand how visitors interact with our content.'
        : 'Wij gebruiken cookies om ervoor te zorgen dat ons platform correct functioneert, om uw voorkeuren te onthouden en om te begrijpen hoe bezoekers onze website gebruiken.',
      sections: [
        {
          h: isEn ? '1. What are cookies?' : '1. Wat zijn cookies?',
          p: isEn 
            ? 'Cookies are small text files stored on your device when you visit a website. They help us recognize your browser and provide a more personalized, efficient experience.'
            : 'Cookies zijn kleine tekstbestanden die op uw apparaat worden opgeslagen wanneer u een website bezoekt. Ze helpen ons uw browser te herkennen en een meer gepersonaliseerde, efficiënte ervaring te bieden.'
        },
        {
          h: isEn ? '2. Functional Cookies' : '2. Functionele cookies',
          p: isEn 
            ? 'These cookies are essential for the operation of our website. For example, they allow the Testament Scan to remember your previous answers as you navigate between steps.'
            : 'Deze cookies zijn essentieel voor de werking van onze website. Ze zorgen er bijvoorbeeld voor dat de Testament Scan uw eerdere antwoorden onthoudt terwijl u door de stappen navigeert.'
        },
        {
          h: isEn ? '3. Analytical Cookies (Google Analytics)' : '3. Analytische cookies (Google Analytics)',
          p: isEn 
            ? 'We use analytical cookies to collect anonymous information about how visitors use our site. This helps us improve our interface and content. We have configured Google Analytics to anonymize IP addresses to protect your identity.'
            : 'Wij gebruiken analytische cookies om anonieme informatie te verzamelen over hoe bezoekers onze site gebruiken. Dit helpt ons de interface en inhoud te verbeteren. We hebben Google Analytics zo geconfigureerd dat IP-adressen worden geanonimiseerd om uw identiteit te beschermen.'
        },
        {
          h: isEn ? '4. Managing Cookies' : '4. Cookies beheren',
          p: isEn 
            ? 'You can choose to disable cookies through your browser settings. Please note that disabling functional cookies may impact the performance of our tools and scans.'
            : 'U kunt ervoor kiezen om cookies uit te schakelen via uw browserinstellingen. Houd er rekening mee dat het uitschakelen van functionele cookies de werking van onze tools en scans kan beïnvloeden.'
        }
      ]
    },
    terms: {
      title: isEn ? 'Terms & Conditions' : 'Algemene Voorwaarden',
      metaTitle: isEn ? 'Terms & Conditions | Testamentair.com' : 'Algemene Voorwaarden | Testamentair.com',
      metaDesc: isEn ? 'Read our terms of service regarding the use of the Testamentair platform and our strategic estate analysis tools.' : 'Lees onze servicevoorwaarden met betrekking tot het gebruik van het Testamentair-platform en onze strategische nalatenschapsanalyse-tools.',
      intro: isEn 
        ? 'These terms govern the relationship between you and Testamentair. By using our platform, you agree to these conditions in full.'
        : 'Deze voorwaarden regelen de relatie tussen u en Testamentair. Door gebruik te maken van ons platform, gaat u volledig akkoord met deze voorwaarden.',
      sections: [
        {
          h: isEn ? '1. Nature of Service' : '1. Aard van de dienstverlening',
          p: isEn 
            ? 'Testamentair provides a digital platform for the preparation and strategic analysis of estate planning. We offer software-driven insights and dossiers intended to facilitate your consultation with a notary.'
            : 'Testamentair biedt een digitaal platform voor de voorbereiding en strategische analyse van nalatenschapsplanning. Wij bieden software-gedreven inzichten en dossiers die bedoeld zijn om uw consultatie met een notaris te vergemakkelijken.'
        },
        {
          h: isEn ? '2. No Legal or Notarial Advice' : '2. Geen juridisch of notarieel advies',
          p: isEn 
            ? 'Testamentair is not a law firm or a notary office. The information and dossiers generated by our platform do not constitute legal, fiscal, or notarial advice. A legally binding will or deed can only be executed by a licensed notary.'
            : 'Testamentair is geen advocatenkantoor of notariskantoor. De informatie en dossiers die door ons platform worden gegenereerd, vormen geen juridisch, fiscaal of notarieel advies. Een rechtsgeldig testament of akte kan uitsluitend worden gepasseerd door een beëdigd notaris.'
        },
        {
          h: isEn ? '3. User Responsibility' : '3. Verantwoordelijkheid gebruiker',
          p: isEn 
            ? 'The accuracy of our analysis depends on the information you provide. You are responsible for ensuring that all data entered into our platform is complete and truthful.'
            : 'De nauwkeurigheid van onze analyse hangt af van de informatie die u verstrekt. U bent verantwoordelijk voor de volledigheid en waarheidsgetrouwheid van alle gegevens die u op ons platform invoert.'
        },
        {
          h: isEn ? '4. Intellectual Property' : '4. Intellectueel eigendom',
          p: isEn 
            ? 'All content on this platform, including logic, algorithms, text, and design, is the property of Testamentair and is protected by intellectual property laws.'
            : 'Alle inhoud op dit platform, inclusief logica, algoritmen, tekst en ontwerp, is eigendom van Testamentair en wordt beschermd door intellectuele eigendomsrechten.'
        },
        {
          h: isEn ? '5. Limitation of Liability' : '5. Aansprakelijkheid',
          p: isEn 
            ? 'Testamentair shall not be liable for any damages resulting from decisions made based on the information provided on our platform without consulting a legal professional.'
            : 'Testamentair is niet aansprakelijk voor enige schade die voortvloeit uit beslissingen genomen op basis van de informatie op ons platform zonder raadpleging van een juridisch professional.'
        }
      ]
    },
    disclaimer: {
      title: 'Disclaimer',
      metaTitle: isEn ? 'Disclaimer | Testamentair.com - Legal Notice' : 'Disclaimer | Testamentair.com - Juridische Mededeling',
      metaDesc: isEn ? 'Important legal notice regarding the informational nature of the content on Testamentair.com.' : 'Belangrijke juridische mededeling over de informatie die op dit platform wordt verstrekt.',
      intro: isEn 
        ? 'This disclaimer outlines the limitations of the information provided on Testamentair.com. Please read this carefully before proceeding.'
        : 'Deze disclaimer beschrijft de beperkingen van de informatie op Testamentair.com. Lees dit zorgvuldig door voordat u verder gaat.',
      sections: [
        {
          h: isEn ? '1. Informational Purposes' : '1. Informatieve doeleinden',
          p: isEn 
            ? 'The content on this website is intended for general informational purposes only. While we strive to maintain accurate and up-to-date information, legislation regarding inheritance and taxes is subject to frequent changes.'
            : 'De inhoud van deze website is uitsluitend bedoeld voor algemene informatieve doeleinden. Hoewel wij streven naar nauwkeurige en actuele informatie, is wetgeving rondom erfrecht en belastingen onderhevig aan frequente wijzigingen.'
        },
        {
          h: isEn ? '2. No Guarantees' : '2. Geen garanties',
          p: isEn 
            ? 'We make no warranties or representations, express or implied, about the completeness, accuracy, or suitability of the information for any specific purpose.'
            : 'Wij geven geen garanties of verklaringen, expliciet of impliciet, over de volledigheid, nauwkeurigheid of geschiktheid van de informatie voor welk specifiek doel dan ook.'
        },
        {
          h: isEn ? '3. Personal Responsibility' : '3. Persoonlijke verantwoordelijkheid',
          p: isEn 
            ? 'Any reliance you place on such information is strictly at your own risk. Testamentair is a preparatory tool, not a substitute for professional legal advice.'
            : 'Elk vertrouwen dat u stelt in dergelijke informatie is strikt op eigen risico. Testamentair is een voorbereidend hulpmiddel, geen vervanging voor professioneel juridisch advies.'
        },
        {
          h: isEn ? '4. Regulatory Changes' : '4. Wijzigingen in regelgeving',
          p: isEn 
            ? 'Inheritance laws and tax treaties vary by jurisdiction and can change. We recommend that you always verify the results of our scan with a qualified notary or tax advisor.'
            : 'Erfrecht en belastingverdragen verschillen per jurisdictie en kunnen veranderen. Wij raden u aan de resultaten van onze scan altijd te verifiëren bij een beëdigd notaris of belastingadviseur.'
        }
      ]
    }
  };

  const page = content[type];

  // Set SEO Metadata
  useEffect(() => {
    if (page) {
      document.title = page.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', page.metaDesc);
    }
  }, [page]);

  return (
    <main className="w-full bg-white pb-32">
      {/* Header Section */}
      <header className="relative pt-24 pb-28 overflow-hidden border-b border-slate-50">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-50 rounded-full blur-3xl opacity-40 -z-10"></div>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            {/* Added mb-10 for breathing room, leading-[1.15] for descender safety, and pb-2 to prevent clipping */}
            <H1 className="mb-10 text-gradient leading-[1.15] pb-2">{page.title}</H1>
            <Text className="max-w-2xl mx-auto text-lg text-slate-500 leading-relaxed">
              {page.intro}
            </Text>
          </motion.div>
        </div>
      </header>

      {/* Content Section */}
      <article className="max-w-3xl mx-auto px-6 pt-20">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.8 }} className="space-y-20">
          {page.sections.map((section, idx) => (
            <section key={idx} className="group">
              <h2 className="text-xl font-bold text-navy-900 mb-6 flex items-center gap-4">
                <span className="w-1.5 h-1.5 rounded-full bg-navy-900 group-hover:scale-150 transition-transform" />
                {section.h}
              </h2>
              <div className="text-slate-600 leading-relaxed space-y-4">
                <p className="text-[15px] md:text-base">{section.p}</p>
              </div>
              {/* Divider Line */}
              <div className="h-px w-full bg-slate-100/60 mt-12" />
            </section>
          ))}
          
          {/* Professional Callout */}
          <section className="bg-slate-50 p-8 md:p-10 rounded-[2rem] border border-slate-100">
            <p className="text-sm md:text-base text-navy-900 font-semibold leading-relaxed">
              {isEn 
                ? "For specific legal or notarial matters, we recommend consulting a qualified professional."
                : "Voor specifieke juridische of notariële vragen raden wij aan contact op te nemen met een gekwalificeerde professional."}
            </p>
          </section>

          {/* Timestamp */}
          <div className="pt-8 text-center md:text-left">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">
              {isEn ? 'Last Updated: May 2026' : 'Laatst bijgewerkt: Mei 2026'}
            </p>
          </div>
        </motion.div>
      </article>
    </main>
  );
};

export default LegalPage;